/**
 * @file Registration.jsx
 * @description This file contains the registration form for new users. Located at /register route.
 * Reponsible for rendering the form, handling form submission, and communicating with the backend API.
 * Uses react-hook-form for form management and validation.
 * @author Brady Holland
 */

import React, { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import ButtonPrimary from '../Components/subcomponents/ButtonPrimary';
import Select from '../Components/subcomponents/Select';
import currentDomain from '../Utils/Utils';
import ConfirmationModal from '../Components/Modals/ConfirmationModal';
import Information from '../Components/Global/Information';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { toast } from 'react-toastify';
//module "/mnt/f/riderank/web/src/services/api"
// Registration Component
const Registration = ({ preReg }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dayOfReg = searchParams.get('dayOfReg') === 'true';

  const registrationDefaultValues = {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      phone: '',
      email: '',
      selectedSize: '',
      vehicles: [{ year: '', make: '', model: '', engine: '', club: '', vehicleType: 1, specialfeatures: '', inClass: '', parkingZone: 0, isPaid: dayOfReg  }],
    }

  const navigate = useNavigate();
  // Derived from the prop on every render — App fetches the prereg flag
  // asynchronously, so capturing it in a useState initializer would freeze the
  // stale default and leave a closed registration open (or vice versa).
  const showForm = preReg || dayOfReg;
  const [regData, setRegData] = React.useState(registrationDefaultValues);
  const [confirmShowing, setConfirmShowing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // A ref, not just the state flag: two taps in the same tick both read the
  // stale `submitting === false` before React re-renders, so state alone does
  // not stop a real double-tap. The state exists only to drive the disabled UI.
  const submittingRef = React.useRef(false);
  // One key per confirm-modal session. Retrying the SAME confirm reuses it and
  // the server replays the first response; going back and submitting again
  // mints a new one, so adding another vehicle still works. A ref so re-renders
  // don't regenerate it mid-flight.
  const idempotencyKeyRef = React.useRef(null);

  

  // Initialize the form
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: registrationDefaultValues,
  });


  /**
   * @property {Object} fields - The array of vehicle fields managed by useFieldArray.
   * @property {Function} append - Function to append a new vehicle field to the form.
   * @property {Function} remove - Function to remove a vehicle field from the form at a specified index.
  */
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'vehicles',
  });

  // Handlers called by add Vehicle button to append a new vehicle to the form
  const handleAddVehicle = (event) => {

    event.preventDefault();
    append({
      year: '',
      make: '',
      model: '',
      engine: '',
      club: '',
      vehicleType: 1, // Ensure that this defaults to "Car"
      specialfeatures: '',
      inClass: '',
      parkingZone: 0,
      isPaid: dayOfReg 
    });
    console.log('Adding vehicle...');
  };


  // Handler called by remove vehicle button to remove a vehicle from the form at specified index
  const handleRemoveVehicle = (index) => {
    console.log('Removing vehicle...');
    if (fields.length > 1) {
      remove(index); //Calls remove function from useFieldArray to remove vehicle at index
    }
  };

  // crypto.randomUUID is secure-context only: fine over https and on
  // localhost, but undefined over plain http to a LAN IP, which is a plausible
  // day-of tablet setup.
  const newIdempotencyKey = () =>
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;

  const submitConfirmed = async (data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // Send the form data to the backend API
      const response = await fetch(`${currentDomain(location)}/api/register`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify(data),
      });

      // Read the body exactly once. Errors come back as plain text, so
      // response.json() would throw before we ever reached the ok check.
      const raw = await response.text();

      if (!response.ok) {
        toast.error(`Registration failed. ${raw}`);
        return;
      }

      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        body = {};
      }
      console.log('Response body:', body);

      if (dayOfReg) {
        data.vehicles.forEach((vehicle, index) => {
          toast.success(`${vehicle.year} ${vehicle.make} ${vehicle.model} - Car Number: ${body.carNums?.[index] ?? 'Error: Ask Brady'}`, { autoClose: false });
        });
        setConfirmShowing(false);
        apiClient.sendTextMessage(1, [{owner_phone: data.phone, year: '', make: '', model: ''}])
          .catch((e) => console.error('SMS send failed', e));
        reset(registrationDefaultValues); // Clear the form
        navigate(`/registration?dayOfReg=true`);

      } else {
        setConfirmShowing(false);
        navigate(`/checkout?phone=${encodeURIComponent(data.phone)}`);
      }
    } catch (error) {
      toast.error(`Registration failed. ${error?.message ?? error}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  // Everyone registering is US-based, but some people type their number with
  // the +1 country code, and the checkout lookup matches on exactly 10 digits.
  // Collapse "+1 (913) 704-8131" and friends to "9137048131" silently rather
  // than rejecting the form over it.
  const normalizePhone = (value) => {
    const digits = (value ?? '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  };

  // Form submission handler
  const onSubmit = async (data) => {
    //TODO: Conditional logic for logging
    console.log('Form Data:', data);
    data.phone = normalizePhone(data.phone);
    setRegData(data);
    // Mint the key here, not in submitConfirmed, so retrying a failed confirm
    // reuses it while a fresh trip through the form gets a new one.
    idempotencyKeyRef.current = newIdempotencyKey();
    setConfirmShowing(true);
  };


  const onError = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const displayRegistrationForm = () => {
    return (

      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        // onSubmit={handleSubmit(onSubmitDebug, onSubmitDebug)}
        className="mx-8 border shadow-xl px-8"
      >
        <div>
          <h1 className="font-bold text-2xl pt-8">Car Show Pre-Registration Form</h1>
        </div>

        {/* Personal Information */}
        <div className="lg:flex gap-4">
          <div className="lg:w-2/3 w-full">
            <TextInput
              label="First Name"
              name="firstName"
              reqSymbol
              {...register('firstName', { required: 'First name is required' })}
              error={errors.firstName?.message}
            />
          </div>
          <div className="lg:w-1/3 w-full">
            <TextInput
              label="Last Name"
              name="lastName"
              reqSymbol
              {...register('lastName', { required: 'Last name is required' })}
              error={errors.lastName?.message}
            />
          </div>
        </div>

        {(!dayOfReg) && (<div className="lg:flex gap-4">
          <div className="lg:w-2/3 w-full">
            <TextInput
              label="Address"
              name="address"
              reqSymbol
              {...register('address', { required: 'Address is required' })}
              error={errors.address?.message}
            />
          </div>
          <div className="lg:w-1/3 w-full">
            <TextInput
              label="City"
              name="city"
              reqSymbol
              {...register('city', { required: 'City is required' })}
              error={errors.city?.message}
            />
          </div>
          <div className="lg:w-1/3 w-full">
            <div className="pt-4 pb-2">
              <label className="text-lg font-semibold">State<span className="text-red-500">*</span></label>
              <select
                className={`w-full border ${errors.state ? 'border-red-500' : 'border-gray-300'} p-2 pb-3`}
                {...register('state', { required: 'State is required' })}
              >
                <option disabled value="">Select a state</option>
                {['Kansas', 'Missouri', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
                  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
                  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
                  'West Virginia', 'Wisconsin', 'Wyoming'].map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
              </select>
              {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state.message}</p>}
            </div>
          </div>
        </div>)}
        <div className='lg:flex gap-4'>
          <div className={`${!dayOfReg ? 'lg:w-1/3 w-full' : 'lg:w-1/2 w-full'}`}>
            <TextInput
              label="Phone"
              name="phone"
              reqSymbol
              {...register('phone', { required: 'Phone number is required' })}
              error={errors.phone?.message}
            />

          </div>
          <div className={`${!dayOfReg ? 'lg:w-1/3 w-full' : 'lg:w-1/2 w-full'}`}>
            <TextInput
              label="Email"
              name="email"
              reqSymbol={!dayOfReg}
              {...register('email', { required: !dayOfReg ? 'Email is required' : false })}
              error={errors.phone?.message}
            />
          </div>
          <div className="lg:w-1/3 w-full">
            {!dayOfReg && (<div className="pt-4 pb-2">
              <label className="text-lg font-semibold">TShirt Size<span className="text-red-500">*</span></label>
              <select
                className={`w-full border ${errors.selectedSize ? 'border-red-500' : 'border-gray-300'} p-2 pb-3`}
                value={watch('selectedSize')}
                {...register('selectedSize', { required: 'Size is required' })}
              >
                <option disabled value="">Select T-Shirt Size</option>
                {['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {errors.selectedSize && <p className="text-red-500 text-sm mt-1">{errors.selectedSize.message}</p>}
            </div>)}
          </div>
        </div>
        {!dayOfReg &&(<div className="">
          <input
            type="checkbox"
          />
          <label className="text-sm"> Opt-in for SMS updates </label>
          <Information tooltipText="I agree to receive event-day SMS updates from Basehor VFW Car Show from 913 705-5182; Up to 10 msgs on Sep 13th. Msg & data rates may apply. Reply STOP to cancel. YOUR INFORMATION WILL NOT BE SHARED" />
        </div>)}
        <div className="h-px w-full bg-black my-7" />

        {/* Vehicles Section */}
        <div className="text-2xl text-center font-bold">Vehicles</div>
        {fields.map((vehicle, index) => (
          <React.Fragment key={vehicle.id}>
            <div className="relative">
              {index > 0 && (
                <button
                  type="button"
                  className="absolute top-0 right-0 text-red-500"
                  onClick={() => handleRemoveVehicle(index)}
                >
                 <b>Remove Vehicle</b> 
                </button>
              )}

              <VehicleInput
                index={index}
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
                watch={watch}
                dayOfReg={dayOfReg}
              />
            </div>
            {index < fields.length - 1 && <div className="h-px w-full bg-black my-7" />}
          </React.Fragment>
        ))}

        {/* Add Vehicle Button */}
        <div className="flex justify-center pt-4">
          <ButtonPrimary onClick={handleAddVehicle} size="large" type="button">
            + Add Vehicle
          </ButtonPrimary>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-6">
          <ButtonPrimary color="bg-blue-500 hover:bg-blue-700 my-8" type="submit">
            Submit
          </ButtonPrimary>
        </div>
      </form>
    );
  }


  return (
    <div className="max-w-4xl mx-auto pt-8 pb-20">
      {confirmShowing && (
        <ConfirmationModal
          isOpen={confirmShowing}
          title="Confirm Registration"
          defaultValues={regData}
          onGoBack={() => { if (!submittingRef.current) setConfirmShowing(false); }}
          onConfirm={() => submitConfirmed(regData)}
          showParkingZone={dayOfReg}
          submitting={submitting}
        />
      )}
      {showForm && displayRegistrationForm()}
      {!showForm && (
        <div className="mx-8 border shadow-xl px-8 py-20 text-center">
          <h1 className="font-bold text-2xl">Pre-Registration has closed for 2026</h1>
          <p className="pt-4">You can still register on the day of the event at the registration desk.</p>
          <p className="pt-4">We look forward to seeing you there!</p>
        </div>
      )}
    </div>
  )
};


// Vehicle Input Component. Deliberately at module scope: defining it inside
// Registration would create a new component type on every parent render,
// remounting every field per keystroke (dropping focus and resetting the
// class/type dropdowns' internal state).
const VehicleInput = ({ index, register, errors, control, setValue, watch, dayOfReg }) => {

  return (
    <div>
      <div className="lg:flex gap-4">
        <div className="lg:w-1/3 w-full">
          <TextInput
            label="Year"
            name={`vehicles[${index}].year`}
            error={errors?.vehicles?.[index]?.year?.message}
            reqSymbol
            {...register(`vehicles[${index}].year`, { required: 'Year is required' })}
          />
        </div>
        <div className="lg:w-1/3 w-full">
          <TextInput
            label="Make"
            name={`vehicles[${index}].make`}
            error={errors?.vehicles?.[index]?.make?.message}
            reqSymbol
            {...register(`vehicles[${index}].make`, { required: 'Make is required' })}
          />
        </div>
        <div className="lg:w-1/3 w-full">
          <TextInput
            label="Model"
            name={`vehicles[${index}].model`}
            error={errors?.vehicles?.[index]?.model?.message}
            reqSymbol
            {...register(`vehicles[${index}].model`, { required: 'Model is required' })}
          />
        </div>
      </div>

      <div className="lg:flex gap-4">
        <div className="lg:w-1/3 w-full">
          <TextInput
            label="Engine"
            placeholder="ex: 6.2L Supercharged V8"
            name={`vehicles[${index}].engine`}
            error={errors?.vehicles?.[index]?.engine?.message}
            reqSymbol={!dayOfReg}
            {...register(`vehicles[${index}].engine`, { required: !dayOfReg ? 'Engine is required' : false })}
          />
        </div>
        <div className="lg:w-1/3 w-full">
          <TextInput
            label="Club"
            name={`vehicles[${index}].club`}
            {...register(`vehicles[${index}].club`)}
          />
        </div>


        {/* Vehicle Type Select */}
        <div className="lg:w-1/3 w-full">
          <Controller
            name={`vehicles[${index}].vehicleType`}
            control={control}
            rules={{ required: 'Vehicle type is required' }}
            render={({ field }) => (
              <Select.VehicleType
                value={field.value}
                onChange={field.onChange}
                error={errors?.vehicles?.[index]?.vehicleType?.message}

              />
            )}
          />
        </div>


      </div>

      <div className="lg:flex gap-4">
        {/* Special Features */}
        <div className={`${dayOfReg ? 'lg:w-1/2' : 'lg:w-2/3'} w-full`}>
          <TextInput
            label="Special Features"
            name={`vehicles[${index}].specialfeatures`}
            {...register(`vehicles[${index}].specialfeatures`)}
          />
        </div>

        {/* Parking Zone Select - Only show if dayOfReg */}
        {dayOfReg && (
          <div className="lg:w-2/5 w-full pt-2">
            <Controller
              name={`vehicles[${index}].parkingZone`}
              control={control}
              rules={{ required: dayOfReg ? 'Parking zone is required' : false }}
              render={({ field }) => (
                <Select.ParkingZones
                  value={field.value}
                  index={index}
                  onChange={(selectedZone) => {
                    setValue(`vehicles[${index}].parkingZone`, selectedZone);
                    field.onChange(selectedZone);
                  }}
                   displayTitle={true}
                />
              )}
            />
          </div>
        )}

        {/* Vehicle Class Select */}
        <div className={`lg:w-1/3 w-full`}>
          <Controller
            name={`vehicles[${index}].inClass`}
            control={control}
            rules={{ required: 'Vehicle class is required' }}
            render={({ field }) => (
              <div>
                <Select.VehicleClass
                  value={field.value}  // The current value from form state
                  index={index}
                  onChange={(selectedClass) => {
                    setValue(`vehicles[${index}].inClass`, selectedClass);  // Update form state on change
                    field.onChange(selectedClass);  // Make sure to update field value
                  }}
                  error={errors?.vehicles?.[index]?.inClass?.message}
                  watchf={watch}
                />
              </div>
            )}
          />

        </div>
      </div>
    </div>
  );
};


// TextInput Component
const TextInput = forwardRef(({ label, name, error, reqSymbol, ...rest }, ref) => (
  <div className="pt-4 pb-2">
    <label className="pb-1 text-lg font-semibold">{label}{reqSymbol && <span className="text-red-500">*</span>}</label>
    <input
      ref={ref}
      name={name}
      className={`w-full border ${error ? 'border-red-500' : 'border-gray-300'} p-2`}
      {...rest}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
));





// Export Registration component
export default Registration;